import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, X } from 'lucide-react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { computeGranularity, type TimeSeriesResult } from '@/hooks/useTimeseriesQuery';
import {
  type SeriesConfig,
  defaultSeries,
  seriesLabel,
  migrateLegacySeriesList,
} from '@/lib/analytics/seriesConfig';
import { MetricPicker } from './MetricPicker';
import { SavedViewsMenu } from './SavedViewsMenu';
import {
  type SharedSeriesArgs,
  CallsFetcher,
  MeaningfulCallsFetcher,
  PersonalEmailsFetcher,
  CampaignEmailsFetcher,
  NewContactsFetcher,
  NewConnectionsFetcher,
  EmailsFetcher,
} from './SeriesFetchers';
import { useSavedAnalyticsViews } from '@/hooks/useSavedAnalyticsViews';
import { buildMergedChartData } from './customChartData';
import { formatBucketTick } from '@/lib/analytics/buckets';

type ChartType = 'line' | 'bar' | 'area';

const SERIES_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

interface CustomChartCardProps {
  shared: SharedSeriesArgs;
  outcomeOptions: ReadonlyArray<{ value: string; label: string }>;
  relationshipOptions: ReadonlyArray<{ value: string; label: string }>;
  workspaceId: string;
  userId: string;
  isAdmin: boolean;
}

export function CustomChartCard({
  shared,
  outcomeOptions,
  relationshipOptions,
  workspaceId,
  userId,
  isAdmin,
}: CustomChartCardProps) {
  const [chartType, setChartType] = useState<ChartType>('line');
  const [series, setSeries] = useState<SeriesConfig[]>(defaultSeries);
  const [seriesData, setSeriesData] = useState<Record<string, TimeSeriesResult>>({});

  // Auto-apply the workspace_default saved view on first load if one exists.
  // We only do this once per workspace id; later edits keep whatever the user
  // configured in the picker.
  const { views } = useSavedAnalyticsViews(workspaceId);
  const appliedDefaultRef = useRef<string | null>(null);
  useEffect(() => {
    if (appliedDefaultRef.current === workspaceId) return;
    if (views.length === 0) return;
    const def = views.find((v) => v.is_workspace_default);
    if (def?.config?.series && def.config.series.length > 0) {
      setSeries(migrateLegacySeriesList(def.config.series));
      if (def.config.chartType) setChartType(def.config.chartType);
    }
    appliedDefaultRef.current = workspaceId;
  }, [workspaceId, views]);

  const handleData = useCallback((id: string, result: TimeSeriesResult) => {
    // Fetchers fire onData only when scalar invariants change, so we can
    // unconditionally store the latest result here.
    setSeriesData((prev) => ({ ...prev, [id]: result }));
  }, []);

  // Prune stale entries when series are removed.
  useEffect(() => {
    setSeriesData((prev) => {
      const next: Record<string, TimeSeriesResult> = {};
      for (const s of series) if (prev[s.id]) next[s.id] = prev[s.id];
      return next;
    });
  }, [series]);

  // Derive the chart's date axis from the user-selected from/to so the chart
  // always spans the selected period — even if all series have empty data, or
  // some have data only at one edge of the range. Without this, the axis
  // would collapse to the union of dates that happen to be present in
  // seriesData, snapping the chart to the data extent.
  const granularity = computeGranularity(shared.from, shared.to, shared.manualGranularity);
  const mergedData = useMemo(
    () => buildMergedChartData(shared.from, shared.to, granularity, series, seriesData),
    [shared.from, shared.to, granularity, series, seriesData],
  );

  const isLoading = series.length > 0 && series.some((s) => seriesData[s.id]?.isLoading ?? true);
  const error = series.map((s) => seriesData[s.id]?.error ?? null).find((e) => e !== null) ?? null;
  const isEmpty =
    !isLoading &&
    !error &&
    series.length > 0 &&
    series.every((s) => (seriesData[s.id]?.total_current ?? 0) === 0);

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h2 className="text-sm font-medium">Compare metrics</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={chartType} onValueChange={(v) => setChartType(v as ChartType)}>
            <SelectTrigger className="w-24 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="line">Line</SelectItem>
              <SelectItem value="bar">Bar</SelectItem>
              <SelectItem value="area">Area</SelectItem>
            </SelectContent>
          </Select>
          <SavedViewsMenu
            workspaceId={workspaceId}
            userId={userId}
            isAdmin={isAdmin}
            currentConfig={{ series, chartType }}
            onLoad={(cfg) => {
              if (cfg.series) setSeries(migrateLegacySeriesList(cfg.series));
              if (cfg.chartType) setChartType(cfg.chartType);
            }}
          />
          <MetricPicker
            series={series}
            onChange={setSeries}
            outcomeOptions={outcomeOptions}
            relationshipOptions={relationshipOptions}
          />
        </div>
      </div>

      {series.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {series.map((s, idx) => (
            <Badge
              key={s.id}
              variant="secondary"
              className="flex items-center gap-1 pl-2 pr-1"
              style={{
                borderLeft: `3px solid ${SERIES_COLORS[idx % SERIES_COLORS.length]}`,
              }}
            >
              <span className="text-xs">{seriesLabel(s)}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={() => setSeries((prev) => prev.filter((x) => x.id !== s.id))}
                aria-label={`Remove ${seriesLabel(s)}`}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}

      {/* Hidden fetchers: one per series, each calls its own hook. */}
      {series.map((s) => {
        switch (s.kind) {
          case 'calls':
            return (
              <CallsFetcher
                key={s.id}
                id={s.id}
                shared={shared}
                outcomes={s.outcomes}
                onData={handleData}
              />
            );
          case 'meaningful_calls':
            return (
              <MeaningfulCallsFetcher key={s.id} id={s.id} shared={shared} onData={handleData} />
            );
          case 'personal_emails':
            return (
              <PersonalEmailsFetcher key={s.id} id={s.id} shared={shared} onData={handleData} />
            );
          case 'campaign_emails':
            return (
              <CampaignEmailsFetcher key={s.id} id={s.id} shared={shared} onData={handleData} />
            );
          case 'emails':
            return (
              <EmailsFetcher
                key={s.id}
                id={s.id}
                shared={shared}
                subtypes={s.subtypes}
                onData={handleData}
              />
            );
          case 'new_contacts':
            return (
              <NewContactsFetcher key={s.id} id={s.id} shared={shared} onData={handleData} />
            );
          case 'new_connections':
            return (
              <NewConnectionsFetcher
                key={s.id}
                id={s.id}
                shared={shared}
                relationships={s.relationships}
                onData={handleData}
              />
            );
        }
      })}

      {series.length === 0 && (
        <div className="flex items-center justify-center h-80 text-muted-foreground text-sm">
          Add a metric from the picker to start charting.
        </div>
      )}
      {series.length > 0 && isLoading && <Skeleton className="h-80 w-full" />}
      {error && (
        <div className="flex flex-col items-center justify-center h-80 gap-2">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          <p className="text-sm text-muted-foreground">Couldn't load</p>
        </div>
      )}
      {isEmpty && (
        <div className="flex items-center justify-center h-80 text-muted-foreground text-sm">
          Not enough data yet
        </div>
      )}
      {series.length > 0 && !isLoading && !error && !isEmpty && (
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={mergedData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              dataKey="bucket_date"
              tickFormatter={(s: string) => formatBucketTick(s, granularity)}
              fontSize={12}
              stroke="hsl(var(--muted-foreground))"
            />
            <YAxis fontSize={12} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
            <Tooltip
              contentStyle={{
                background: 'hsl(var(--popover))',
                border: '1px solid hsl(var(--border))',
                color: 'hsl(var(--popover-foreground))',
              }}
            />
            <Legend />
            {series.map((s, idx) =>
              renderSeries(
                chartType,
                s.id,
                seriesLabel(s),
                SERIES_COLORS[idx % SERIES_COLORS.length],
              ),
            )}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

function renderSeries(type: ChartType, key: string, label: string, color: string) {
  if (type === 'line') {
    return <Line key={key} dataKey={key} name={label} stroke={color} type="monotone" dot={false} />;
  }
  if (type === 'area') {
    return (
      <Area
        key={key}
        dataKey={key}
        name={label}
        stroke={color}
        fill={color}
        fillOpacity={0.2}
        type="monotone"
      />
    );
  }
  return <Bar key={key} dataKey={key} name={label} fill={color} />;
}
